import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const RiversideCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in Riverside, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Riverside, CA. Serving Downtown Riverside, Mission Inn area, Canyon Crest, UCR, and all neighborhoods. Premium auto detailing at your location.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - Riverside",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Riverside",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "Riverside",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 33.9806,
        "longitude": -117.3755
      },
      "description": "Professional mobile car detailing service in Riverside, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Interior Reset', link: '/services' },
    { name: 'Paint Enhancement', link: '/services' },
    { name: 'Maintenance Package', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Do you provide mobile detailing near UCR campus in Riverside?',
      answer: 'Yes! We serve the University of California Riverside campus area, student housing, and all surrounding neighborhoods. Mobile detailing is perfect for busy students and faculty who need convenient car care.'
    },
    {
      question: 'Can you detail cars in Downtown Riverside and the Mission Inn area?',
      answer: 'Absolutely. We provide mobile car detailing throughout Downtown Riverside, the historic Mission Inn district, and all central Riverside locations. We come directly to your home, office, or parking area.'
    },
    {
      question: 'How much does mobile car detailing cost in Riverside, CA?',
      answer: 'Our pricing depends on the service package and vehicle size. We offer competitive rates for Riverside customers with transparent pricing on our Services page. Packages start for sedans and scale up for SUVs and trucks.'
    },
    {
      question: 'Do you serve Canyon Crest, Orangecrest, and La Sierra?',
      answer: 'Yes! We provide mobile detailing services throughout Canyon Crest, Orangecrest, La Sierra, Arlington, and all Riverside neighborhoods. Our service area covers the entire city and surrounding communities.'
    },
    {
      question: 'What do I need to provide for mobile detailing in Riverside?',
      answer: 'We bring everything required for a complete detail. All you need to provide is a safe parking spot near your vehicle. We supply water, equipment, and all products.'
    },
    {
      question: 'How long does a typical mobile detail take?',
      answer: 'Most mobile detailing services take 2 to 6 hours depending on the package and vehicle condition. We work efficiently while delivering top-quality results at your Riverside location.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://cdn.britannica.com/05/152405-050-EABDBE62/Riverside-California.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Riverside, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Mobile Car Detailing in Riverside, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Premium mobile auto detailing delivered throughout Riverside. 
              From Downtown and the Mission Inn area to Canyon Crest, UCR campus, and beyond, we bring professional car care to your location.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book Riverside Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in Riverside */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Mobile Detailing Riverside CA Drivers Trust</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Riverside is a vibrant, sprawling city with diverse neighborhoods and busy residents who value their time. 
                Precision Mobile Detailing understands this and brings professional car detailing directly to your Riverside location, 
                whether you are downtown near the historic Mission Inn, at home in Canyon Crest, or on campus at UCR.
              </p>
              <p>
                Our mobile detailing service eliminates the inconvenience of traditional car washes and detailing shops. 
                No more scheduling your day around drop-offs and pick-ups. Instead, we arrive fully equipped at your home, 
                workplace, apartment complex, or anywhere in Riverside with everything needed for a complete, high-quality detail.
              </p>
              <p>
                From Orangecrest to La Sierra, Arlington to Woodcrest, we serve every corner of Riverside with the same 
                commitment to excellence. Our team uses professional-grade products and techniques to restore your vehicle 
                to showroom condition while you focus on work, family, or simply relaxing at home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Mobile Detailing Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">How Our Mobile Service Works in Riverside</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Schedule Online or Call</h3>
                <p className="text-gray-600">Choose your detailing package and preferred appointment time. We offer flexible scheduling throughout Riverside to fit your busy life.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">We Come to You</h3>
                <p className="text-gray-600">Our mobile team arrives at your Riverside address with all equipment, water supply, and premium products. You provide nothing but a parking spot.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Get Back a Perfect Vehicle</h3>
                <p className="text-gray-600">Go about your day while we work. When complete, your car looks and feels brand new, and you saved time by not visiting a shop.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Riverside */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Auto Detailing Services in Riverside, CA</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From quick refreshes to full transformations, we offer mobile detailing packages designed for Riverside drivers.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          Learn More →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  View Full Service Menu & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Riverside Chooses Precision Mobile Detailing</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Save Time</h3>
                  <p className="text-gray-600">No driving to shops, no waiting rooms. We bring professional detailing to your Riverside location so you can stay productive.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Expert Results</h3>
                  <p className="text-gray-600">Professional-grade products and techniques ensure your vehicle receives the best care possible. Every detail matters to us.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">5.0 Star Service</h3>
                  <p className="text-gray-600">Trusted by customers across the Inland Empire. Reliable, punctual, and committed to exceeding expectations every time.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within Riverside */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Riverside Neighborhoods We Serve</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Mobile car detailing throughout Riverside and surrounding communities.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Downtown Riverside', 'Mission Inn District', 'Canyon Crest', 'UCR Campus', 'Orangecrest', 'La Sierra', 'Arlington', 'Woodcrest'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              We also provide mobile detailing in nearby <Link to="/service-areas/rancho-cucamonga-ca" className="text-black font-semibold hover:underline">Rancho Cucamonga</Link>, <Link to="/service-areas/san-bernardino-ca" className="text-black font-semibold hover:underline">San Bernardino</Link>, and <Link to="/service-areas/redlands-ca" className="text-black font-semibold hover:underline">Redlands</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Mobile Detailing FAQs for Riverside</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Mobile Detailing in Riverside Today</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Professional car detailing delivered to your doorstep. Schedule your Riverside appointment now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Get a Quote <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RiversideCA;
